import type { AdminAccessResponse } from '../../../../api';
import { useAdminAccess } from '../../../../hooks/useAdminAccess';
import { capabilityReleaseMode } from '../../../../utils/capabilityRelease';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../redux/hooks';
import ROLES from '../../../../utils/roles';

/**
 * Who may open Capability Setup, by the backend's rule: an org admin of the
 * org, a manager of one of its teams, or a platform admin. A program admin who
 * manages no team may not. `access` is GET /v3/admin/access; when that call
 * failed (null), an org admin's own role stands in for it.
 */
export const canOpenCapabilitySetup = (
  access: AdminAccessResponse | null,
  isPlatformAdmin: boolean,
  role: string
): boolean => {
  if (isPlatformAdmin) return true;
  if (!access) return role === ROLES.orgAdmin;
  return access.orgAdmin || access.managedTeamIds.length > 0;
};

/**
 * The same for the signed-in user (the route guard): null until the org and
 * /v3/admin/access have answered. False for an org without the Capability
 * release (utils/capabilityRelease.ts). Decided on the org's mode, not on
 * useCapabilityRelease(): in admin mode that one waits on the same access call,
 * so while it loads it reads false and the guard sent org admins and team
 * managers to /unauthorized. Setup is for them in either mode.
 */
export const useCapabilitySetupAccess = (): boolean | null => {
  const org = useAppSelector((state) => state.orgDetails?.data as Parameters<typeof capabilityReleaseMode>[0]);
  const orgKnown = Boolean(org?.authId);
  const { isPlatformAdmin } = useProgramContext();
  const { access, loading } = useAdminAccess();
  if (!orgKnown) return null;
  if (capabilityReleaseMode(org) === 'off') return false;
  if (isPlatformAdmin) return true;
  if (loading) return null;
  return canOpenCapabilitySetup(access, false, localStorage.getItem('role') || '');
};
